import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "../RightSidebar/rightsidebar.module.css";
import PrimaryButton from "../Buttons/PrimaryButton";
import DropdownImageTrigger from "../DropDown/DropdownImageTrigger";
import menuIcon from "../../Assets/images/menu.svg";
import {
  appendExpandedTimeLabel,
  getInitials,
  getConsistentColor,
  removedSeconds,
  scrollToBookingCard,
} from "../../Utils/utils";
import {
  resolveDiscountOption,
  resolveCheckoutMemberRateOption,
  getDiscountDropdownSelectedOption,
  hasBookingPriceAdjustmentObject,
  isFullDiscount,
} from "./checkoutPricingModifiers";
import { resolveFinalCommissionRate } from "../../Utils/commissionRateResolver";
import { useCheckoutPricingModifiers } from "./useCheckoutPricingModifiers";
import Guesticon from "../../Assets/images/guestIcon.svg";
import { useGlobalContext } from "../../Context/GlobalContext";
import PrimiumMemberIcon from "../Icons/PrimiumMemberIcon";
import { RequestedTherapist } from "../Badges/RequestedTherapist";
import DropDownIcon from "../Icons/DropDownIcon";
import { SaleNote } from "../SaleNote/SaleNote";
import DiscountDropdown from "../DropDown/PriceAdjustmentDropdowns/DiscountDropdown";
import { createSale, postApplyDiscountToBooking } from "../../APICalling/APICalling";
import CommissionInlineEdit from "./CommissionInlineEdit/CommissionInlineEdit";
import MemberRateDropdown from "../DropDown/PriceAdjustmentDropdowns/MemberRateDropdown";
import { PAYMENT_IN_PROGRESS_MESSAGE } from "../../Utils/Constants/errorMessages";
import { getBookingPreventChanges } from "../../Utils/BookingPreventChanges";
import logger from "../../Utils/logger";
import BookingInfoLoader from "./BookingInfoLoader/BookingInfoLoader";
import Skeleton from "react-loading-skeleton";
import ReloadIcon from "../Icons/ReloadIcon";
import { isPrimaryCustomerGroup } from "../../Utils/liveGuestCheckinTag";

const CheckoutPage = ({
  onNext,
  onBack,
  discardPaymentPopup,
  hideHeader = false,
  isSalesFlow = false,
  showAddNotePopup,
  saleNote,
  disableServiceListHover = false,
}) => {
  const {
    bookingDetails, setBookingDetails,
    customerBookingItems, setCustomerBookingItems,
    serviceTotal,
    setServiceTotal,
    setVoucherDetail,
    voucherStates,
    setVoucherStates,
    discountByBookingItemId,
    setDiscountByBookingItemId,
    manualCommissionByBookingItemId,
    setManualCommissionByBookingItemId,
    clearedEditedCommissionByBookingItemId,
    setClearedEditedCommissionByBookingItemId,
    publicHoliday,
    discountItemToPost, setDiscountItemToPost,
    selectedOutletData,
    selectedDate,
    setIsBookingInfoUpdated,
    setSidebarLoading,
    memberRateByBookingItemId,
    setMemberRateByBookingItemId,
    isAutoApply,
    setIsAutoApply,
  } = useGlobalContext();

  const isPremiumOutlet = selectedOutletData?.outlet_type_id === 1; // 1 = Premium, 2 = Standard


  logger.log("bookingDetails", bookingDetails);

  const [appliedResponse, setAppliedResponse] = useState(null);

  // Derive MemberRateDropdown selected option from booking and user override
  const getMemberRateSelectedOption = (booking, id) => {
    return resolveCheckoutMemberRateOption(
      memberRateByBookingItemId[id],
      discountByBookingItemId?.[id],
      booking
    );
  };

  // Get base price from price_rate based on member rate selection
  const getBasePriceForBooking = (booking, id) => {
    const pr = booking?.price_rate;
    const priceRate = !pr ? null : typeof pr === "object" ? pr : (() => { try { return JSON.parse(pr); } catch { return null; } })();
    const memberRateOption = getMemberRateSelectedOption(booking, id);
    const selectedId = memberRateOption?.item?.id;
    if (priceRate) {
      if (selectedId === 2) return parseFloat(priceRate.member_price_before_6_pm || 0);
      if (selectedId === 1) return parseFloat(priceRate.member_after_6_or_weekends || 0);
      return parseFloat(priceRate.nonMemberPrice || booking.originalPrice || booking.price || 0);
    }
    return parseFloat(booking.originalPrice || booking.price || 0);
  };

  // Get addon price from addon_rate based on member rate selection (same logic as price_rate)
  const getAddonPriceFromAddonRate = (addon, selectedId) => {
    const ar = addon?.addon_rate;
    const addonRate = !ar ? null : typeof ar === "object" ? ar : (() => { try { return JSON.parse(ar); } catch { return null; } })();
    if (!addonRate) return parseFloat(addon?.unit_price || 0);
    if (selectedId === 2) return parseFloat(addonRate.member_price_before_6_pm || 0);
    if (selectedId === 1) return parseFloat(addonRate.member_after_6_or_weekends || 0);
    return parseFloat(addonRate.nonMemberPrice || addon?.unit_price || 0);
  };

  // Map addons to include "price" field based on member rate selection
  const getUpdatedAddons = (booking, id) => {
    const memberRateOption = getMemberRateSelectedOption(booking, id);
    const selectedId = memberRateOption?.item?.id;
    return (booking?.addons || []).map((addon) => {
      const unitPrice = getAddonPriceFromAddonRate(addon, selectedId);
      const quantity = parseFloat(addon?.quantity || 1);
      const totalPrice = unitPrice * quantity;
      return {
        ...addon,
        price: totalPrice.toFixed(2),
      };
    });
  };

  // Sum of all addon prices for a booking (based on member rate selection)
  const getTotalAddonPrice = (booking, id) => {
    const updatedAddons = getUpdatedAddons(booking, id);
    const total = updatedAddons.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
    return Math.round(total * 100) / 100;
  };

  // Match discountItem.addons with updatedAddons and apply discount on matched addons. Returns effectivePriceAddonPrice.
  const getEffectiveAddonPrice = (booking, id) => {
    const updatedAddons = getUpdatedAddons(booking, id);
    const discountItem = resolveDiscountOption(discountByBookingItemId?.[id], booking);
    if (!discountItem?.addons?.length) {
      return getTotalAddonPrice(booking, id);
    }
    const discountAddonIds = new Set((discountItem.addons || []).map((a) => a.id));
    let effectiveTotal = 0;
    const matchedAddons = updatedAddons.filter((addon) => discountAddonIds.has(addon.id));
    const matchedTotal = matchedAddons.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
    
    if (discountItem.type === "percentage") {
      updatedAddons.forEach((addon) => {
        const addonPrice = parseFloat(addon.price || 0);
        effectiveTotal += discountAddonIds.has(addon.id)
          ? getEffectivePrice(addonPrice, discountItem)
          : addonPrice;
      });
    } else {
      const basePrice = getBasePriceForBooking(booking, id);
      const totalBase = basePrice + getTotalAddonPrice(booking, id);
      const discountAmount = Math.abs(parseFloat(discountItem.amount ?? discountItem.value ?? 0) || 0);
      const addonShare = totalBase > 0 ? matchedTotal / totalBase : 0;
      const addonDiscountAmount = discountAmount * addonShare;
      const effect = discountItem.effect === "discount" ? -1 : 1;

      updatedAddons.forEach((addon) => {
        const addonPrice = parseFloat(addon.price || 0);
        if (!discountAddonIds.has(addon.id)) {
          effectiveTotal += addonPrice;
        } else {
          const share = matchedTotal > 0 ? addonPrice / matchedTotal : 0;
          const thisAddonDiscount = addonDiscountAmount * share;
          effectiveTotal += Math.max(0, addonPrice + effect * thisAddonDiscount);
        }
      });
    }
    return Math.round(effectiveTotal * 100) / 100;
  };

  const getAppliedResponseAddonTotal = (booking) => {
    const appliedItems = Array.isArray(appliedResponse?.items) ? appliedResponse.items : [];
    const matchedItem = appliedItems.find((item) => {
      const bookingItemId = Number(item?.booking_item_id);
      const currentBookingId = Number(booking?.id);
      return Number.isFinite(bookingItemId) && Number.isFinite(currentBookingId) && bookingItemId === currentBookingId;
    });

    const matchedAddonTotal = matchedItem?.addons_total;
    if (matchedAddonTotal !== undefined && matchedAddonTotal !== null && matchedAddonTotal !== "") {
      return parseFloat(matchedAddonTotal) || 0;
    }

    const topLevelAddonTotal = appliedResponse?.addons_total;
    // Only use top-level fallback when API does not return per-item rows.
    if (
      appliedItems.length === 0 &&
      topLevelAddonTotal !== undefined &&
      topLevelAddonTotal !== null &&
      topLevelAddonTotal !== ""
    ) {
      return parseFloat(topLevelAddonTotal) || 0;
    }

    return null;
  };

  const getEffectiveAddonPriceForBooking = (booking) => {
    const bookingId = booking?.id;
    const appliedAddonTotal = getAppliedResponseAddonTotal(booking);
    if (appliedAddonTotal !== null) return appliedAddonTotal;

    if (booking?.price_adjustment_id) {
      return parseFloat(booking?.addons_total || 0);
    }
    return getEffectiveAddonPrice(booking, bookingId);
  };

  // Compute effective price: discount = minus, surcharge = plus
  // Recompute amount from basePrice when discount is percentage-based (so it updates when basePrice changes)
  const getEffectivePrice = (basePrice, discountItem) => {
    const price = parseFloat(basePrice || 0);
    if (!discountItem) return price;
    let amount;
    if (discountItem.type === "percentage") {
      const percentValue = parseFloat(discountItem.value || 0);
      amount = (percentValue / 100) * price;
    } else {
      amount = parseFloat(discountItem.amount ?? discountItem.value ?? 0);
    }
    if (isNaN(amount)) return price;
    amount = Math.abs(amount);
    if (discountItem.effect === "discount") return Math.max(0, price - amount);
    if (discountItem.effect === "surcharge") return price + amount;
    return price;
  };


  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplyingPriceAdjustment, setIsApplyingPriceAdjustment] = useState(false);
  const [isInitialCheckoutLoadComplete, setIsInitialCheckoutLoadComplete] = useState(false);
  const [discountError, setDiscountError] = useState("");
  const [collapsedCommissions, setCollapsedCommissions] = useState(new Set());
  const isCommissionExpanded = (key) => !collapsedCommissions.has(key);
  const [showPaymentInProgressNotice, setShowPaymentInProgressNotice] = useState(false);
  const [paymentInProgressNoticeHeight, setPaymentInProgressNoticeHeight] = useState(0);
  const shouldAutoApplyPriceAdjustmentRef = useRef(false);
  const hasAutoAppliedForBookingLoadRef = useRef(false);
  const latestPriceAdjustmentRequestRef = useRef(0);
  const paymentInProgressNoticeRef = useRef(null);
  const stableDisplayedRowTotalsRef = useRef({});

  const { isBookingPaymentInProgress, isBookingCompleted } = getBookingPreventChanges(bookingDetails);

  const showPaymentInProgress = () => {
    if (!isBookingPaymentInProgress) return;
    setShowPaymentInProgressNotice(true);
  };

  // Keep scrollable checkout content above the fixed payment-in-progress banner.
  useEffect(() => {
    const visible = isBookingPaymentInProgress && showPaymentInProgressNotice;
    if (!visible) {
      setPaymentInProgressNoticeHeight(0);
      return undefined;
    }

    const noticeEl = paymentInProgressNoticeRef.current;
    if (!noticeEl) {
      setPaymentInProgressNoticeHeight(0);
      return undefined;
    }

    const updateHeight = () => {
      setPaymentInProgressNoticeHeight(Math.ceil(noticeEl.getBoundingClientRect().height || 0));
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(noticeEl);
    return () => resizeObserver.disconnect();
  }, [isBookingPaymentInProgress, showPaymentInProgressNotice, discountError]);


  const toggleCommission = (bookingKey) => {
    setCollapsedCommissions((prev) => {
      const next = new Set(prev);
      if (next.has(bookingKey)) next.delete(bookingKey);
      else next.add(bookingKey);
      return next;
    });
  };

  const parseEditedCommission = (value) => {
    if (value == null || value === "") return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Tier default (member evening / OP / non-member) when discount does not force Member OP commission
  const getTierDefaultCommissionRate = (booking) => {
    const memberRateOption = getMemberRateSelectedOption(booking, booking?.id);
    const title = memberRateOption?.item?.title ?? "";
    if (title === "Member Rate") {
      return parseFloat(booking?.memberCommissionAfter6) || 0;
    }
    if (title === "Member Rate (OP)") {
      return parseFloat(booking?.memberCommissionBefore6) || 0;
    }
    return parseFloat(booking?.nonMemberCommission) || 0;
  };

  const getTierBasedCommissionBaseRate = (booking) => {
    const discountItem = resolveDiscountOption(discountByBookingItemId?.[booking?.id], booking);
    if (isFullDiscount(discountItem)) {
      return parseFloat(booking?.nonMemberCommission) || 0;
    }
    return resolveFinalCommissionRate({
      bookingDate: booking?.service_date || bookingDetails?.service_date,
      bookingTime: booking?.start_time || booking?.service_time,
      isDiscountApplied: discountItem != null,
      isPublicHoliday: publicHoliday,
      defaultCommissionRate: getTierDefaultCommissionRate(booking),
      memberOpCommissionRate: parseFloat(booking?.memberCommissionBefore6) || 0,
    });
  };

  const getResolvedCommissionBaseRate = (booking) => {
    return getTierBasedCommissionBaseRate(booking);
  };

  const getCalculatedCommission = (booking) => {
    const rawBase = getResolvedCommissionBaseRate(booking);
    const baseCommissionRate = parseFloat(rawBase) || 0;
    const addonCommissionRate =
      parseFloat(booking?.commission_addons?.total) ||
      parseFloat(booking?.commission?.addon_commission) ||
      0;
    return baseCommissionRate + addonCommissionRate;
  };

  const getEffectiveCommission = (booking) => {
    const id = booking?.id;
    if (id != null && manualCommissionByBookingItemId[id] != null) {
      return manualCommissionByBookingItemId[id];
    }
    if (id == null || !clearedEditedCommissionByBookingItemId[id]) {
      const editedAmount = parseEditedCommission(booking?.edited_commission);
      if (editedAmount != null) return editedAmount;
    }
    return getCalculatedCommission(booking);
  };

  const isCommissionEdited = (booking) => {
    const id = booking?.id;
    if (id == null) return false;
    if (manualCommissionByBookingItemId[id] != null) return true;
    if (clearedEditedCommissionByBookingItemId[id]) return false;
    return parseEditedCommission(booking?.edited_commission) != null;
  };

  const getCommissionPayloadFields = (booking) => {
    const id = booking?.id;
    const calculatedAmount = getCalculatedCommission(booking);
    if (id != null && clearedEditedCommissionByBookingItemId[id]) {
      return {
        commission_amount: calculatedAmount,
        edited_commission: null,
      };
    }
    if (id != null && manualCommissionByBookingItemId[id] != null) {
      return {
        commission_amount: manualCommissionByBookingItemId[id],
        edited_commission: manualCommissionByBookingItemId[id],
      };
    }
    const editedAmount = parseEditedCommission(booking?.edited_commission);
    if (editedAmount != null) {
      return {
        commission_amount: editedAmount,
        edited_commission: editedAmount,
      };
    }
    return {
      commission_amount: calculatedAmount,
      edited_commission: null,
    };
  };


  const getServiceTimeDisplay = (booking) => {
		let displayTime = "";
		if (booking?.service_time) {
			displayTime = removedSeconds(booking.service_time);
		} else if (booking?.start_time) {
			const [h, m] = String(booking.start_time).split(':');
			const hour = parseInt(h, 10);
			const h12 = hour % 12 || 12;
			const ampm = hour >= 12 ? 'PM' : 'AM';
			displayTime = `${String(h12).padStart(2, '0')}:${(m || '00').slice(0, 2)} ${ampm}`;
		} else if (booking?.service_at) {
			const part = booking.service_at.split(' ')[1];
			if (part) {
				const [hh, mm] = part.split(':');
				const hour = parseInt(hh, 10);
				const h12 = hour % 12 || 12;
				const ampm = hour >= 12 ? 'PM' : 'AM';
				displayTime = `${String(h12).padStart(2, '0')}:${(mm || '00').slice(0, 2)} ${ampm}`;
			}
		}
		const labelContextDate = !isSalesFlow
			? (selectedDate || booking?.service_date || bookingDetails?.service_date)
			: (booking?.service_date || bookingDetails?.service_date);
		return appendExpandedTimeLabel(
			displayTime,
			booking,
			selectedOutletData?.timing,
			labelContextDate
		);
	};


  // Transform booking_item to group split services under parent services
  const transformCustomerData = (customerData) => {
  if (!customerData) return {};
  
  const transformedData = {};
  
  Object.entries(customerData).forEach(([customerName, bookings]) => {
    const transformedBookings = [];
    const processedBookingIds = new Set();
    
    bookings.forEach(booking => {
    if (processedBookingIds.has(booking?.id)) return;
    
    const splited = booking.splited === true || booking.splited === "true";
    
    if (splited && booking.parent_booking_item_id === null) {
      const childServices = bookings.filter(childBooking => 
      childBooking.parent_booking_item_id === booking?.id && 
      (childBooking.splited === true || childBooking.splited === "true")
      );
      childServices.forEach(child => processedBookingIds.add(child.id ?? child.booking?.id));
      transformedBookings.push({ ...booking, SplitedService: childServices });
    } else if (booking.splited === false || booking.splited === "false" || booking.parent_booking_item_id !== null) {
      const isChildOfSplitService = bookings.some(parentBooking => 
      (parentBooking.id ?? parentBooking.booking?.id) === booking.parent_booking_item_id &&
      (parentBooking.splited === true || parentBooking.splited === "true")
      );
      if (!isChildOfSplitService) transformedBookings.push(booking);
    }
    });
    
    transformedData[customerName] = transformedBookings;
  });
  
  return transformedData;
  };

  const { onMemberRateSelect, onDiscountSelect, onGlobalMembershipToggle } =
    useCheckoutPricingModifiers({
      setMemberRateByBookingItemId,
      setDiscountByBookingItemId,
      setCustomerBookingItems,
      customerBookingItems,
      setBookingDetails,
      transformCustomerData,
      bookingDetails,
      publicHoliday,
      isPremiumOutlet,
    });

  const queuePriceAdjustmentApply = useCallback(() => {
    shouldAutoApplyPriceAdjustmentRef.current = true;
    setIsApplyingPriceAdjustment(true);
    setDiscountError("");
  }, []);

  const clearPricingOverrides = useCallback(() => {
    setDiscountByBookingItemId({});
    setMemberRateByBookingItemId({});
    setManualCommissionByBookingItemId({});
    setClearedEditedCommissionByBookingItemId({});
    setDiscountItemToPost([]);
    setAppliedResponse(null);
    setDiscountError("");
  }, [setClearedEditedCommissionByBookingItemId,setDiscountByBookingItemId, setDiscountItemToPost,setManualCommissionByBookingItemId,setMemberRateByBookingItemId,]);

  const handleReload = useCallback(() => {
    clearPricingOverrides();
    setIsBookingInfoUpdated(true);
    if (typeof onBack === "function") onBack();
  }, [clearPricingOverrides,setIsBookingInfoUpdated, onBack]);

  const handleMemberRateSelect = useCallback((booking, item) => {
    queuePriceAdjustmentApply();
    onMemberRateSelect(booking, item);
  }, [onMemberRateSelect, queuePriceAdjustmentApply]);

  const handleDiscountSelect = useCallback((booking, item) => {
    queuePriceAdjustmentApply();
    onDiscountSelect(booking, item);
  }, [onDiscountSelect, queuePriceAdjustmentApply]);

  const handleGlobalMembershipToggle = useCallback((isChecked) => {
    queuePriceAdjustmentApply();
    onGlobalMembershipToggle(isChecked);
  }, [onGlobalMembershipToggle, queuePriceAdjustmentApply]);

  const getDisplayDiscountItem = useCallback((booking) => {
    // Prevent UI flicker by not mixing old applied addon totals with a new local discount
    // while the apply request is still in flight.
    if (isApplyingPriceAdjustment) {
      return resolveDiscountOption(undefined, booking);
    }
    return resolveDiscountOption(discountByBookingItemId?.[booking?.id], booking);
  }, [discountByBookingItemId, isApplyingPriceAdjustment]);

  const handleCommissionSave = useCallback((booking, amount) => {
    queuePriceAdjustmentApply();
    const bookingId = booking?.id;
    if (bookingId == null) return;
    setClearedEditedCommissionByBookingItemId((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    setManualCommissionByBookingItemId((prev) => ({ ...prev, [bookingId]: amount }));
    setCollapsedCommissions((prev) => {
      if (!prev.has(bookingId)) return prev;
      const next = new Set(prev);
      next.delete(bookingId);
      return next;
    });
  }, [queuePriceAdjustmentApply, setClearedEditedCommissionByBookingItemId, setManualCommissionByBookingItemId]);

  const handleCommissionReload = useCallback((booking) => {
    queuePriceAdjustmentApply();
    const bookingId = booking?.id;
    if (bookingId == null) return;
    setManualCommissionByBookingItemId((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    setClearedEditedCommissionByBookingItemId((prev) => ({ ...prev, [bookingId]: true }));
  }, [queuePriceAdjustmentApply, setClearedEditedCommissionByBookingItemId, setManualCommissionByBookingItemId]);

  const UpdatedCustomerBookingItems = useMemo(
    () => transformCustomerData(customerBookingItems),
    [customerBookingItems]
  );

  useEffect(() => {
    if (isApplyingPriceAdjustment) return;
    if (!UpdatedCustomerBookingItems) {
      stableDisplayedRowTotalsRef.current = {};
      return;
    }

    const nextStableTotals = {};
    Object.values(UpdatedCustomerBookingItems).forEach((bookings) => {
      (bookings || []).forEach((booking) => {
        const bookingId = booking?.id;
        if (bookingId == null) return;

        const basePrice = getBasePriceForBooking(booking, bookingId);
        const discountItem = resolveDiscountOption(
          discountByBookingItemId?.[bookingId],
          booking
        );
        const effectivePrice = getEffectivePrice(basePrice, discountItem);
        const effectiveAddonPrice = getEffectiveAddonPriceForBooking(booking);
        const totalEffectivePrice = effectivePrice + effectiveAddonPrice;
        nextStableTotals[bookingId] = Math.round(totalEffectivePrice * 100) / 100;
      });
    });

    stableDisplayedRowTotalsRef.current = nextStableTotals;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cache only depends on pricing state, not helper identities
  }, [
    isApplyingPriceAdjustment,
    UpdatedCustomerBookingItems,
    discountByBookingItemId,
    memberRateByBookingItemId,
    voucherStates,
    appliedResponse,
  ]);

  // Check if all bookings have Member Rate or Member Rate (OP) applied
  const allBookingsHaveMemberRate = () => {
    if (!UpdatedCustomerBookingItems || typeof UpdatedCustomerBookingItems !== "object") return false;
    const allBookings = [];
    Object.values(UpdatedCustomerBookingItems).forEach((bookings) => {
      (bookings || []).forEach((booking) => allBookings.push(booking));
    });
    if (allBookings.length === 0) return false;
    return allBookings.every((booking) => {
      const option = getMemberRateSelectedOption(booking, booking?.id);
      return option?.item?.id === 1 || option?.item?.id === 2;
    });
  };


  logger.log("appliedResponse", appliedResponse);


  useEffect(() => {
    if (!UpdatedCustomerBookingItems) return;
    // Calculate serviceTotal from sum of effective prices per booking (service + addons)
    let total = 0;
    Object.values(UpdatedCustomerBookingItems).forEach((bookings) => {
      bookings.forEach((booking) => {
        const bookingId = booking?.id;
        const basePrice = getBasePriceForBooking(booking, bookingId);
        const discountItem = getDisplayDiscountItem(booking);
        const isVoucherRedeemed = voucherStates?.[booking?.id]?.status === "redeemed";
        const effectiveAddonPrice = getEffectiveAddonPriceForBooking(booking);
        const computedTotal = getEffectivePrice(basePrice, discountItem) + effectiveAddonPrice;
        const stableDisplayedRowTotal = stableDisplayedRowTotalsRef.current?.[bookingId];
        const displayedTotal =
          isApplyingPriceAdjustment && Number.isFinite(stableDisplayedRowTotal)
            ? stableDisplayedRowTotal
            : computedTotal;

        if (isVoucherRedeemed) {
          total += effectiveAddonPrice; // Service is free, addons still apply
        } else {
          total += displayedTotal;
        }
      });
    });
    setServiceTotal(Math.round(total * 100) / 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- UpdatedCustomerDataFromAPI is derived, customerDataFromAPI covers updates
  }, [customerBookingItems, voucherStates, discountByBookingItemId, memberRateByBookingItemId, appliedResponse, isApplyingPriceAdjustment, setServiceTotal]);

  // Initialize original prices for all booking items
  useEffect(() => {
    if (UpdatedCustomerBookingItems) {
      Object.values(UpdatedCustomerBookingItems).forEach(bookings => {
        bookings.forEach(booking => {
          // Set originalPrice if it doesn't exist yet
          if (!booking.originalPrice && booking.price) {
            booking.originalPrice = parseFloat(booking.price);
          }
        });
      });
    }
  }, [UpdatedCustomerBookingItems]);


  useEffect(() => {
    if (!isBookingPaymentInProgress) {
      setShowPaymentInProgressNotice(false);
    }
  }, [isBookingPaymentInProgress]);
  
  // const menuOptions = [
  //   {
  //     label: "Discard Checkout",
  //     tooltip: isBookingPaymentInProgress ? "Payment in Progress" : "",
  //     action: discardPaymentPopup,
  //     disabled: isBookingPaymentInProgress,
  //   },
  // ];

 

  const redeemVoucher = (svc, vs) => {
    const discountedAmount = parseFloat(svc.price || 0);
    svc.originalPrice = svc.price;
    svc.price = 0;
    setVoucherStates((prev) => ({
      ...prev,
      [svc.id]: { ...prev[svc?.id], status: "redeemed", code: vs.code },
    }));
    setServiceTotal((prev) => {
      const newTotal = prev - discountedAmount;
      return Math.round(newTotal * 100) / 100; // Round to 2 decimal places
    });
    setVoucherDetail((prev) => [
      ...(prev || []),
      {
      id: svc?.id,
      code: vs.code || "",
      amount: discountedAmount,
      },
    ]);
	};


	const removeVoucher = (svc) => {
		// Use the stored original price, fallback to current price if not available
		const original = parseFloat(svc.originalPrice || svc.price || 0);
		svc.price = original;
		// Clear the stored original price
		delete svc.originalPrice;
		setVoucherStates((prev) => ({
			...prev,
			[svc?.id]: { code: "", status: "initial" },
		}));
		setServiceTotal((prev) => {
			const newTotal = prev + original;
			return Math.round(newTotal * 100) / 100; // Round to 2 decimal places
		});
		setVoucherDetail((prev) => (prev || []).filter((v) => v.id !== svc?.id));
	};

  const bookingHasPriceAdjustmentObject = useMemo(
    () => hasBookingPriceAdjustmentObject(bookingDetails?.booking_item),
    [bookingDetails?.booking_item]
  );

  useEffect(() => {
    hasAutoAppliedForBookingLoadRef.current = false;
    setIsInitialCheckoutLoadComplete(false);
  }, [bookingDetails?.id]);

  useEffect(() => {
    if (!bookingDetails?.id) return;
    if (bookingDetails?.booking_item == null) return;
    setIsInitialCheckoutLoadComplete(true);
  }, [bookingDetails?.id, bookingDetails?.booking_item]);



  logger.log("checkout >> isAutoApply", isAutoApply);
  logger.log("checkout >> bookingHasPriceAdjustmentObject", bookingHasPriceAdjustmentObject);




  const applyPriceAdjustments = useCallback(async (itemsToApply, requestId = null) => {
    if (!bookingDetails?.id) return null;
    const response = await postApplyDiscountToBooking(
      bookingDetails.id,
      { items: Array.isArray(itemsToApply) ? itemsToApply : [] },
    );
    if (response?.errors || (response?.success === false)) {
      const errorFromArray = Array.isArray(response?.errors)
        ? response.errors?.[0]?.message
        : "";
      const errorFromMap = response?.errors && typeof response.errors === "object"
        ? Object.values(response.errors)?.[0]?.[0]
        : "";
      const errorMessage = errorFromArray || response?.message || errorFromMap || "Failed to apply discount";
      throw new Error(errorMessage);
    }
    // Ignore stale request responses to prevent price flicker.
    if (requestId == null || latestPriceAdjustmentRequestRef.current === requestId) {
      const appliedData = response?.data?.data;
      setAppliedResponse(appliedData);
      const rawGst = appliedData?.gst_amount ?? appliedData?.gst;
      const nextGst = rawGst === null || rawGst === undefined || rawGst === "" ? NaN : Number(rawGst);
      if (Number.isFinite(nextGst)) {
        setBookingDetails((prev) => (prev ? { ...prev, gst_amount: nextGst } : prev));
      }
    }
    return response;
  }, [bookingDetails?.id, setBookingDetails]);

  useEffect(() => {
    if (!isInitialCheckoutLoadComplete) return;
    if (!isAutoApply || !bookingHasPriceAdjustmentObject) return;
    if (hasAutoAppliedForBookingLoadRef.current) return;
    if (!Array.isArray(discountItemToPost) || discountItemToPost.length === 0) return;

    hasAutoAppliedForBookingLoadRef.current = true;
    const requestId = latestPriceAdjustmentRequestRef.current + 1;
    latestPriceAdjustmentRequestRef.current = requestId;
    setIsApplyingPriceAdjustment(true);
    setDiscountError("");
    // setSidebarLoading(true);

    applyPriceAdjustments(discountItemToPost, requestId)
      .catch((error) => {
        if (latestPriceAdjustmentRequestRef.current !== requestId) return;
        logger.error("Failed to auto-apply price adjustment:", error);
        setDiscountError(error?.message || "Failed to apply discount");
      })
      .finally(() => {
        if (latestPriceAdjustmentRequestRef.current === requestId) {
          setIsAutoApply(false);
          setIsBookingInfoUpdated(true);
          setIsApplyingPriceAdjustment(false);
          setSidebarLoading(false);
        }
      });
  }, [
    applyPriceAdjustments,
    bookingHasPriceAdjustmentObject,
    discountItemToPost,
    isAutoApply,
    isInitialCheckoutLoadComplete,
    setIsAutoApply,
    setIsBookingInfoUpdated,
    setSidebarLoading,
  ]);

	useEffect(() => {
		if (!UpdatedCustomerBookingItems) return;
		const allBookings = Object.values(UpdatedCustomerBookingItems).flat();
		// Include all booking items so MemberRateDropdown changes are reflected even when no discount is selected
		const itemToPost = allBookings.map((booking) => {
			const discountItem = resolveDiscountOption(
				discountByBookingItemId?.[booking?.id],
				booking
			);
			const basePrice = getBasePriceForBooking(booking, booking?.id);
			const memberRateOption = getMemberRateSelectedOption(booking, booking?.id);
			const selectedId = memberRateOption?.item?.id;
			const isMemberRateApplied = selectedId === 2 ? 1 : 0;
			const isMembershipApplied = selectedId === 1 || selectedId === 2 ? 1 : 0;
			const baseCommissionRate = parseFloat(getResolvedCommissionBaseRate(booking)) || 0;
			const { commission_amount: commissionAmount, edited_commission: isEditedCommission } = getCommissionPayloadFields(booking);

        const discountAddonIds = new Set(
          (discountItem?.addons || [])
            .map((a) => Number(a?.id))
            .filter((id) => Number.isFinite(id))
        );
        logger.log("booking", booking);
        const addons = (booking?.addons || []).map((addon) => {
          const ar = addon?.addon_rate;
          const addonRate = !ar ? null : typeof ar === "object" ? ar : (() => { try { return JSON.parse(ar); } catch { return null; } })();
          let addonPrice = 0;
          if (addonRate) {
            if (selectedId === 2) addonPrice = parseFloat(addonRate.member_price_before_6_pm || 0);
            else if (selectedId === 1) addonPrice = parseFloat(addonRate.member_after_6_or_weekends || 0);
            else addonPrice = parseFloat(addonRate.nonMemberPrice || addon?.unit_price || 0);
          } else {
            addonPrice = parseFloat(addon?.unit_price || 0);
          }
          const discountItemId = Number(discountItem?.id);
          const addonId = Number(addon?.id);
          const addonLevelAdjustmentId = Number(addon?.additional?.price_adjustment?.id);
          const hasAddonLevelAdjustmentMatch =
            Number.isFinite(discountItemId) &&
            Number.isFinite(addonLevelAdjustmentId) &&
            discountItemId === addonLevelAdjustmentId;
          const discountAppliesToAddon =
            Number.isFinite(discountItemId) &&
            (discountAddonIds.has(addonId) || hasAddonLevelAdjustmentMatch);
          return {
            booking_item_addon_id: Number(addon?.booking_item_addon_id),
            price_adjustment_id: discountAppliesToAddon ? Number(discountItem.id) : null,
            price: addonPrice,
          };
        }).filter((addon) => addon.booking_item_addon_id != null);

        logger.log("addons at checkout", addons);
			return {
				booking_item_id: Number(booking?.id),
				price_adjustment_id: discountItem?.id != null ? Number(discountItem.id) : null,
				commission_price_adjustment_id: null,
				price: basePrice,
				isMemberRateApplied,
				isMembershipApplied,
				commission_rate: baseCommissionRate,
				commission_amount: commissionAmount,
				edited_commission: isEditedCommission,
				addons,
			};
		}).filter((item) => item.booking_item_id != null);
		setDiscountItemToPost((prev) => {
      const prevSerialized = JSON.stringify(prev || []);
      const nextSerialized = JSON.stringify(itemToPost);
      if (prevSerialized === nextSerialized) {
        return prev;
      }
      return itemToPost;
    });
    if (shouldAutoApplyPriceAdjustmentRef.current) {
      shouldAutoApplyPriceAdjustmentRef.current = false;
      const requestId = latestPriceAdjustmentRequestRef.current + 1;
      latestPriceAdjustmentRequestRef.current = requestId;
      setIsApplyingPriceAdjustment(true);
      // setIsBookingInfoUpdated(true);
      applyPriceAdjustments(itemToPost, requestId)
        .catch((error) => {
          if (latestPriceAdjustmentRequestRef.current !== requestId) return;
          logger.error("Failed to apply price adjustment:", error);
          setDiscountError(error?.message || "Failed to apply discount");
        })
        .finally(() => {
          if (latestPriceAdjustmentRequestRef.current === requestId) {
            setIsApplyingPriceAdjustment(false);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getBasePriceForBooking/safeParseAdditional are stable; deps cover data changes
	}, [discountByBookingItemId, manualCommissionByBookingItemId, clearedEditedCommissionByBookingItemId, setDiscountItemToPost, UpdatedCustomerBookingItems, memberRateByBookingItemId, publicHoliday, bookingDetails?.service_date, applyPriceAdjustments]);
  
  const handleCreateSale = async () => {
    if (bookingDetails?.sale_id || (bookingDetails?.sale_id && isSalesFlow)) return true;
    const params = {
      booking_id: bookingDetails?.id,
      paid_amount: bookingDetails?.grand_total,
      currency: bookingDetails?.currency || "SGD",
      panel: "outlet",
    };
    try {
      const response = await createSale(params);
      if (response?.success || response?.data?.success) {
        return true;
      }

      const errorFromMap = response?.errors && typeof response.errors === "object"
        ? Object.values(response.errors)?.[0]?.[0]
        : "";
      const errorMessage = response?.message || response?.data?.message || errorFromMap || "Failed to create sale";
      throw new Error(errorMessage);
    } catch (error) {
      logger.error("Error Booking Status:", error);
      throw error;
    }
  };


  if (isAutoApply)
    return (
      <div className={styles.sidebarLoading}>
        <BookingInfoLoader />
      </div>
    );

  return (
    <div className={`${styles.checkout} h-100`}>
      <div className={styles.rightHeader}>
        <div className={`${styles.dFlex} ${styles.justifyBetween}`}>
          <div className={styles.dFlex}>
            <h5>Checkout</h5>
          </div>
        </div>
        {/* <div className={`${styles.menuIcon} ${styles.atCheckout}`}>
          <DropdownImageTrigger
            iconSrc={menuIcon}
            options={menuOptions}
            menuClass={styles.customMenuClass}
          />
        </div> */}
      </div>

      <div
        className={`${styles.infoWrapper} ${styles.checkoutContent}`}
        style={
          paymentInProgressNoticeHeight > 0
            ? { paddingBottom: paymentInProgressNoticeHeight }
            : undefined
        }
      >
          <div className={styles.customerInfoWrapper}>
						<div className={styles.customerCard}>
							<div className={`${styles.avatarWrapper} cursor-pointer`}
							onClick={() => {
								const contactNumber = bookingDetails?.user?.contact_number;

								const userId = bookingDetails?.user?.id;
								if (contactNumber || userId) {
									const customerData = {
										id: userId,
										name: bookingDetails?.user?.name,
										contact_number: contactNumber,
										email: bookingDetails?.user?.email,
									};

									const event = new CustomEvent('openCustomerDetails', {
										detail: { customer: customerData }
									});
									window.dispatchEvent(event);
								}
							}} 
							>
								<div className={styles.avatar} style={{backgroundColor: getConsistentColor(bookingDetails?.user?.id, bookingDetails?.user?.name)}}>
									<span>{getInitials(bookingDetails?.user?.name)}</span>
									{bookingDetails?.user?.membership === 1 && (
										<span className={styles.membershipIcon}>
											<PrimiumMemberIcon/>
										</span>
									)}
								</div>
							</div>
							<div 
								className={styles.customerDetails}>
								<div className="cursor-pointer"
								onClick={() => {
									const contactNumber = bookingDetails?.user?.contact_number;
	
									const userId = bookingDetails?.user?.id;
									if (contactNumber || userId) {
										const customerData = {
											id: userId,
											name: bookingDetails?.user?.name,
											contact_number: contactNumber,
											email: bookingDetails?.user?.email,
										};
	
										const event = new CustomEvent('openCustomerDetails', {
											detail: { customer: customerData }
										});
										window.dispatchEvent(event);
									}
								}}
								>
						    	<p className={styles.customerMobile}>
							    	{bookingDetails?.user?.contact_number}
									</p>
									<p className={styles.customerName}>
									{bookingDetails?.user?.name}
									</p>
								</div>
		

              <label
                className={`${styles.toggleLabel} ${isBookingPaymentInProgress ? styles.disabledToggle : ''}`}
                onClick={showPaymentInProgress}
              >
                  <span className={styles.labelText}>Apply membership discount:</span>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={allBookingsHaveMemberRate()}
                        disabled={isBookingPaymentInProgress}
                        onChange={(e) => handleGlobalMembershipToggle(e.target.checked)}
                      />
                      <span className={styles.slider}></span>
                    </label>
                </label>

								{bookingDetails?.user?.note?.trim() && (
									<div className={styles.customerNotes}>
										<p className={styles.customerNotesText}>
											{bookingDetails?.user?.note}
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

          {/* {saleNote?.trim() && (
              <SaleNote saleNote={saleNote} showAddNotePopup={showAddNotePopup} />
          )} */}

          {(() => {
						// Separate primary customer and guest data
						const primaryCustomerData = [];
						const guestData = [];
            
            if (UpdatedCustomerBookingItems) {
              Object.entries(UpdatedCustomerBookingItems).forEach(([customerName, bookings]) => {
                if (isPrimaryCustomerGroup(customerName, bookings, bookingDetails?.user?.name)) {
                  primaryCustomerData.push({ customerName, bookings });
                } else {
                  guestData.push({ customerName, bookings });
                }
              });
            }
						
		
						return (
              <>
								{/* Primary Customer Data - Show first without guest icon and name */}
								{primaryCustomerData.map(({ customerName, bookings }) => (
									<div key={`primary-${customerName}`} className={styles.paxBlock}>
										{bookings.map((booking, index) => {
                      const basePrice = getBasePriceForBooking(booking, booking?.id);
                      const totalAddonPrice = getTotalAddonPrice(booking, booking?.id);
                      const basePriceWithAddons = basePrice + totalAddonPrice;
                      const discountItem = getDisplayDiscountItem(booking);
                      const effectivePrice = getEffectivePrice(basePrice, discountItem);
                      // const effectiveAddonPrice = getEffectiveAddonPrice(booking, booking?.id);
                      const effectiveAddonPrice = getEffectiveAddonPriceForBooking(booking);

                      const totalEffectivePrice = effectivePrice + effectiveAddonPrice;
                      const stableDisplayedRowTotal = stableDisplayedRowTotalsRef.current?.[booking?.id];
                      const displayedEffectivePrice =
                        isApplyingPriceAdjustment && Number.isFinite(stableDisplayedRowTotal)
                          ? stableDisplayedRowTotal
                          : totalEffectivePrice;
											return (
												<div key={index} className={styles.serviceLine}>
													<div className={`${styles.serviceDetails} ${disableServiceListHover ? styles.serviceDetailsNoHover : ''}`}
														onClick={() => {if (!disableServiceListHover && booking?.id) {scrollToBookingCard(booking?.id);}}}
													>
                          <div
                            className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCommission(booking?.id ?? `primary-${index}`);
                            }}
                          >
                            <div className={`${styles.dFlex} ${styles.toggleService} gap-5`}>
                              <DropDownIcon
                                size={16}
                                color="var(--neutral-900)" 
                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? `primary-${index}`) ? styles.rotated : ""}`}
                              />
                              <div className={`${styles.selectedServiceHead} mb-0`}>
                                <h3 className="mb-0">{`${booking?.duration} Mins ${booking?.service}`}</h3>
                              </div>
                            </div>
                            <p className={styles.price}>
                              {voucherStates?.[booking?.id]?.status === "redeemed" ? (
                                <>
                                  <strike>${basePriceWithAddons.toFixed(2)}</strike>{" "}
                                  <span>${parseFloat(0).toFixed(2)}</span>
                                </>
                              ) : (() => {
                                return discountItem ? (
                                  <>
                                    <strike>${basePriceWithAddons.toFixed(2)}</strike>{" "}
                                    <span>${displayedEffectivePrice.toFixed(2)}</span>
                                  </>
                                ) : (
                                  <span>${displayedEffectivePrice.toFixed(2)}</span>
                                );
                              })()}
                            </p>
                          </div>


                              {booking?.addons?.length > 0 && (
                                <div className={`${styles.dFlex} ${styles.spacing}`}>
                                  <label className={styles.label}>Add-on:</label>
                                  <p className={styles.sameFont}>{booking.addons.map((addon) => addon.name).join(', ')}</p>
                                </div>
                              )}

                            <div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>With:</label>
																<span
																	className={`${styles.therapistCircle} ${
																	(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female : 
																	(booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
																	}`}
																	title={booking?.therapist_pager_number ?? 'N/A'}
																>
																	{booking?.therapist_pager_number ?? 'N/A'}
																</span>
																<p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{booking?.therapist ?? booking?.therapist_name}</p>
															</div>
															{booking?.requested_person === 1 && <RequestedTherapist/>}
														</div>

                            {booking?.commission?.rate && isCommissionExpanded(booking?.id ?? `primary-${index}`) && 
                            <div className={`${styles.commissionCard} ${styles.spacing}`}>
                              <CommissionInlineEdit
                                value={getEffectiveCommission(booking)}
                                maxCommissionPrice={booking?.maximum_commission_price}
                                defaultCommission={getCalculatedCommission(booking)}
                                isEdited={isCommissionEdited(booking)}
                                onSave={(amount) => handleCommissionSave(booking, amount)}
                                onReload={() => handleCommissionReload(booking)}
                                disabled={isBookingCompleted}
                              />
                            </div>
                          }

                          {/* SPLITED SERVICE  START */}
														{booking?.SplitedService?.length > 0 && (
															<div className={styles.splitedService}>
															{booking?.SplitedService?.map((splitedService) => {
																return (
																<div key={splitedService?.id}>
																<div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>With:</label>
																	<span
																		className={`${styles.therapistCircle} ${
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female : 
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
																		}`}
																	>
																		{splitedService?.therapist_pager_number ?? 'N/A'}
																	</span>
																	<p className={styles.sameFont}>{splitedService?.therapist ?? splitedService?.therapist_name}</p>
																	</div>
																	{splitedService?.requested_person === 1 && <RequestedTherapist/>}
																</div>

																<div className={`${styles.dFlex} ${styles.spacing}`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>For:</label>
																	<p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
																	</div>
																	<div className={`${styles.dFlex} ${styles.space}`}>
																	<label className={styles.label}>At:</label>
																	<p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
																	</div>
																</div>
																</div>
																);
															})}
															</div>
														)}
														{/* SPLITED SERVICE  END */}

                           <div className={`${styles.spacing} ${styles.dFlex} ${styles.gap10} mb-0`}>

                            {/* {(() => {
                              const memberRateOption = getMemberRateSelectedOption(booking, booking?.id);
                              if (!memberRateOption?.item) return null;
                              return (
                                <div className={dropdownStyles.selectedDiscount}>
                                  <span className={dropdownStyles.selectedDiscountText}>
                                    {memberRateOption.item?.title ?? "Member Rate"}
                                  </span>
                                  <button
                                    type="button"
                                    className={dropdownStyles.selectedDiscountClose}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMemberRateByBookingItemId((prev) => ({ ...prev, [booking?.id]: null }));
                                    }}
                                    aria-label="Remove Member Rate">
                                    <img src={CrossIcon} alt="" width={10} height={10} />
                                  </button>
                                </div>
                              );
                            })()} */}

                            <div onClick={showPaymentInProgress}>
                              <MemberRateDropdown
                               bookingItemId={booking?.id}
                               serviceId={booking?.service_id}
                               selectedOutletID={booking?.outlet_id}
                               servicePrice={getBasePriceForBooking(booking, booking?.id)}
                               selectedOption={getMemberRateSelectedOption(booking, booking?.id)}
                               onSelect={(item) => handleMemberRateSelect(booking, item)}
                              disabled={isBookingPaymentInProgress}
                               isPremiumOutlet={isPremiumOutlet}
                              />
                            </div>

                            <div onClick={showPaymentInProgress}>
                              <DiscountDropdown
                                bookingItemId={booking?.id}
                                serviceId={booking?.service_id}
                                selectedOutletID={booking?.outlet_id}
                                selectedOption={getDiscountDropdownSelectedOption(
                                  discountByBookingItemId[booking?.id],
                                  booking
                                )}
                                servicePrice={getBasePriceForBooking(booking, booking?.id)}
                                // addOnPrice={getTotalAddonPrice(booking, booking?.id)}
                                selectedAddons={booking?.addons}
                                selectedServicePrice={booking?.price_rate?.nonMemberPrice}
                                onSelect={(item) => handleDiscountSelect(booking, item)}
                                disabled={isBookingPaymentInProgress}
                              />
                            </div>
                            </div>
                          
                          {/* VOUCHER CODE DISABLED FOR NOW */}
                          {/* {(() => {
                            const vs = (voucherStates && voucherStates[booking?.id]) || {
                              code: "",
                              status: "initial",
                            };
                            return (
                              <div className={`${styles.serviceVoucher} ${styles.dFlex} ${styles.justifyBetween}`}>
                                <input
                                  type="text"
                                  placeholder="Enter service voucher code/ reference"
                                  disabled={vs.status === "redeemed"}
                                  value={vs.code}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\s/g, '');
                                    setVoucherStates((prev) => ({
                                      ...prev,
                                      [booking?.id]: { 
                                        code: value, 
                                        status: value.trim() ? "ready" : "initial" 
                                      },
                                    }));
                                  }}
                                />
                                {vs.status === "initial" && (
                                  <AddCircleIcon/>
                                )}
                                {vs.status === "ready" && vs.code.trim() && (
                                  <PrimaryButton
                                    text="Redeem"
                                    className={styles.redeemBtn}
                                    onClick={() => redeemVoucher(booking, vs)}
                                  />
                                )}
                                {vs.status === "redeemed" && (
                                  <BorderedButton
                                    text="Remove"
                                    className={styles.removeBtn}
                                    onClick={() => removeVoucher(booking)}
                                  />
                                )}
                              </div>
                            );
                          })()} */}
												</div>
											</div>
											);
										})}
									</div>
								))}

								
								{/* Guest Data - Show after primary customer data with guest icon and name */}
								{guestData.map(({ customerName, bookings }) => (
									<div key={`guest-${customerName}`} className={styles.paxBlock}>
										<div className={`${styles.dFlex} ${styles.paxHeader}`}>
											<img src={Guesticon} alt="guestIcon" />
											<p className={styles.paxName}>{customerName}</p>
										</div>
										{bookings.map((booking, index) => {
                      const basePrice = getBasePriceForBooking(booking, booking?.id);
                      const totalAddonPrice = getTotalAddonPrice(booking, booking?.id);
                      const basePriceWithAddons = basePrice + totalAddonPrice;
                      const discountItem = getDisplayDiscountItem(booking);
                      const effectivePrice = getEffectivePrice(basePrice, discountItem);
                      // const effectiveAddonPrice = getEffectiveAddonPrice(booking, booking?.id);
                      const effectiveAddonPrice = getEffectiveAddonPriceForBooking(booking);
                      const totalEffectivePrice = effectivePrice + effectiveAddonPrice;
                      const stableDisplayedRowTotal = stableDisplayedRowTotalsRef.current?.[booking?.id];
                      const displayedEffectivePrice =
                        isApplyingPriceAdjustment && Number.isFinite(stableDisplayedRowTotal)
                          ? stableDisplayedRowTotal
                          : totalEffectivePrice;
											return (
												<div key={index} className={styles.serviceLine}>
													<div className={`${styles.serviceDetails} ${disableServiceListHover ? styles.serviceDetailsNoHover : ''}`}
														onClick={() => {if (!disableServiceListHover && booking?.id) {scrollToBookingCard(booking?.id);}}}
													>
													<div
                            className={`${styles.spacing} d-flex justify-between cursor-pointer`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCommission(booking?.id ?? `guest-${customerName}-${index}`);
                            }}
                          >
                            <div className={`${styles.dFlex} ${styles.toggleService} gap-5`}>
                              <DropDownIcon
                                size={16}
                                color="var(--neutral-900)" 
                                className={`${styles.dropdownIcon} ${isCommissionExpanded(booking?.id ?? `guest-${customerName}-${index}`) ? styles.rotated : ""}`}
                              />
                              <div className={`${styles.selectedServiceHead} mb-0`}>
                                <h3 className="mb-0">{`${booking?.duration} Mins ${booking?.service}`}</h3>
                              </div>
                            </div>
                            <p className={styles.price}>
                              {voucherStates?.[booking?.id]?.status === "redeemed" ? (
                                <>
                                  <strike>${basePriceWithAddons.toFixed(2)}</strike>{" "}
                                  <span>${parseFloat(0).toFixed(2)}</span>
                                </>
                              ) : (() => {
                                return discountItem ? (
                                  <>
                                    <strike>${basePriceWithAddons.toFixed(2)}</strike>{" "}
                                    <span>${displayedEffectivePrice.toFixed(2)}</span>
                                  </>
                                ) : (
                                  <span>${displayedEffectivePrice.toFixed(2)}</span>
                                );
                              })()}
                            </p>
                          </div>

                          {booking?.addons?.length > 0 && (
                                <div className={`${styles.dFlex} ${styles.spacing}`}>
                                  <label className={styles.label}>Add-on:</label>
                                  <p className={styles.sameFont}>{booking.addons.map((addon) => addon.name).join(', ')}</p>
                                </div>
                              )}

                            <div className={`${styles.selectedTherapistCard} ${styles.spacing} d-flex justify-between`}>
															<div className={`${styles.dFlex}`}>
																<label className={styles.label}>With:</label>
																<span
																	className={`${styles.therapistCircle} ${
																	(booking?.gender ?? booking?.therapist_gender) === "female" ? styles.female : 
																	(booking?.gender ?? booking?.therapist_gender) === "male" ? styles.male : ''
																	}`}
																	title={booking?.therapist_pager_number ?? 'N/A'}
																>
																	{booking?.therapist_pager_number ?? 'N/A'}
																</span>
																<p className={styles.sameFont} title={(booking?.therapist ?? booking?.therapist_name) ?? 'N/A'}>{booking?.therapist ?? booking?.therapist_name}</p>
															</div>
															{booking?.requested_person === 1 && <RequestedTherapist/>}
														</div>


                            {booking?.commission?.rate && isCommissionExpanded(booking?.id ?? `guest-${customerName}-${index}`) && 
                            <div className={`${styles.commissionCard} ${styles.spacing}`}>
                              <CommissionInlineEdit
                                value={getEffectiveCommission(booking)}
                                maxCommissionPrice={booking?.maximum_commission_price}
                                defaultCommission={getCalculatedCommission(booking)}
                                isEdited={isCommissionEdited(booking)}
                                onSave={(amount) => handleCommissionSave(booking, amount)}
                                onReload={() => handleCommissionReload(booking)}
                                disabled={isBookingCompleted}
                              />
                            </div>
                          }
                            

                          {booking.splited && 
														isCommissionExpanded(booking?.id ?? `guest-${customerName}-${index}`) &&
														(booking?.commission?.rate && 
														<div className={`${styles.commissionCard} ${styles.dFlex} ${styles.spacing}`}>
															<label className={styles.label}>Adjusted Commission (S$):</label>
															<p className={styles.sameFont}>${booking?.commission?.rate}</p>
														</div>
														)}

                         {/* SPLITED SERVICE  START */}
														{booking?.SplitedService?.length > 0 && (
															<div className={styles.splitedService}>
															{booking?.SplitedService?.map((splitedService) => {
																return (
																<div key={splitedService?.id}>
																<div className={`${styles.selectedTherapistCard} d-flex justify-between`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>With:</label>
																	<span
																		className={`${styles.therapistCircle} ${
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "female" ? styles.female : 
																		(splitedService?.gender ?? splitedService?.therapist_gender) === "male" ? styles.male : ''
																		}`}
																	>
																		{splitedService?.therapist_pager_number ?? 'N/A'}
																	</span>
																	<p className={styles.sameFont}>{splitedService?.therapist ?? splitedService?.therapist_name}</p>
																	</div>
																	{splitedService?.requested_person === 1 && <RequestedTherapist/>}
																</div>

																<div className={`${styles.dFlex} ${styles.spacing}`}>
																	<div className={`${styles.dFlex}`}>
																	<label className={styles.label}>For:</label>
																	<p className={styles.sameFont}>{splitedService?.duration ?? splitedService?.duration ?? ''} mins</p>
																	</div>
																	<div className={`${styles.dFlex} ${styles.space}`}>
																	<label className={styles.label}>At:</label>
																	<p className={styles.sameFont}>{getServiceTimeDisplay(splitedService)}</p>
																	</div>
																</div>
																</div>
																);
															})}
															</div>
														)}
														{/* SPLITED SERVICE  END */}

                           <div className={`${styles.spacing} ${styles.dFlex} ${styles.gap10} mb-0`}>

                            {/* {(() => {
                              const memberRateOption = getMemberRateSelectedOption(booking, booking?.id);
                              if (!memberRateOption?.item) return null;
                              return (
                                <div className={dropdownStyles.selectedDiscount}>
                                  <span className={dropdownStyles.selectedDiscountText}>
                                    {memberRateOption.item?.title ?? "Member Rate"}
                                  </span>
                                  <button
                                    type="button"
                                    className={dropdownStyles.selectedDiscountClose}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMemberRateByBookingItemId((prev) => ({ ...prev, [booking?.id]: null }));
                                    }}
                                    aria-label="Remove Member Rate">
                                    <img src={CrossIcon} alt="" width={10} height={10} />
                                  </button>
                                </div>
                              );
                            })()} */}

                            <div onClick={showPaymentInProgress}>
                              <MemberRateDropdown
                               bookingItemId={booking?.id}
                               serviceId={booking?.service_id}
                               selectedOutletID={booking?.outlet_id}
                               servicePrice={getBasePriceForBooking(booking, booking?.id)}
                               selectedOption={getMemberRateSelectedOption(booking, booking?.id)}
                               onSelect={(item) => handleMemberRateSelect(booking, item)}
                              disabled={isBookingPaymentInProgress}
                               isPremiumOutlet={isPremiumOutlet}
                              />
                            </div>

                            <div onClick={showPaymentInProgress}>
                              <DiscountDropdown
                                bookingItemId={booking?.id}
                                serviceId={booking?.service_id}
                                selectedOutletID={booking?.outlet_id}
                                selectedOption={getDiscountDropdownSelectedOption(
                                  discountByBookingItemId[booking?.id],
                                  booking
                                )}
                                servicePrice={getBasePriceForBooking(booking, booking?.id)}
                                // addOnPrice={getTotalAddonPrice(booking, booking?.id)}
                                selectedAddons={booking?.addons}
                                selectedServicePrice={booking?.price_rate?.nonMemberPrice}
                                onSelect={(item) => handleDiscountSelect(booking, item)}
                               disabled={isBookingPaymentInProgress}
                              />
                            </div>
                        </div>

                          {/* VOUCHER CODE DISABLED FOR NOW */}
                            {/* {(() => {
                              const vs = (voucherStates && voucherStates[booking?.id]) || {
                                code: "",
                                status: "initial",
                              };
                              return (
                                <div className={`${styles.serviceVoucher} ${styles.dFlex} ${styles.justifyBetween}`}>
                                  <input
                                    type="text"
                                    placeholder="Enter service voucher code/ reference"
                                    disabled={vs.status === "redeemed"}
                                    value={vs.code}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/\s/g, '');
                                      setVoucherStates((prev) => ({
                                        ...prev,
                                        [booking?.id]: { 
                                          code: value, 
                                          status: value.trim() ? "ready" : "initial" 
                                        },
                                      }));
                                    }}
                                  />
                                  {vs.status === "initial" && (
                                    <AddCircleIcon/>
                                  )}
                                  {vs.status === "ready" && vs.code.trim() && (
                                    <PrimaryButton
                                      text="Redeem"
                                      className={styles.redeemBtn}
                                      onClick={() => redeemVoucher(booking, vs)}
                                    />
                                  )}
                                  {vs.status === "redeemed" && (
                                    <BorderedButton
                                      text="Remove"
                                      className={styles.removeBtn}
                                      onClick={() => removeVoucher(booking)}
                                    />
                                  )}
                                </div>
                              );
                            })()} */}
												</div>
											</div>
											);
										})}
									</div>
								))}
							</>
						);
					})()}

        {/* PAYMENT IN PROGRESS START */}
        {isBookingPaymentInProgress && showPaymentInProgressNotice && (
        <div
          ref={paymentInProgressNoticeRef}
          className={`
          ${styles.overlappingErrorContainer} 
          ${styles.paymentInProgress} 
          ${styles.checkoutInfoContainer}
          ${discountError ? styles.withError : ''}
          `}
        >
          {PAYMENT_IN_PROGRESS_MESSAGE}
        </div>
        )}
        {/* PAYMENT IN PROGRESS END */}

        {/* DISCOUNT ERROR START */}
        {discountError && (
          <div className={`${styles.overlappingErrorContainer} ${styles.checkoutError}`}>
            <div className={styles.saleInProgressErrorRow}>
              <div className={styles.errorMessage}>{discountError}</div>
                              <button
                type="button"
                className={styles.reloadErrorButton}
                onClick={handleReload}
              >
                <ReloadIcon size={12} color="currentColor" />
                Reload
              </button>
            </div>
          </div>
        )}
        <div className={`${styles.totalContainer}`}>
          <div className={`${styles.dFlex} ${styles.justifyBetween} ${styles.p20}`}>
            <p>Subtotal:</p>
            <p>${serviceTotal?.toFixed(2)}</p>
          </div>
        </div>



        <div className={styles.bookBtnContainer}>
          <PrimaryButton
            text={isProcessing ? "Processing..." : "Go to Payments"}
            className={`${styles.fullWidthBtn} rounded-0`}
            type="button"
            disabled={isProcessing || Boolean(discountError) || isApplyingPriceAdjustment}
            onClick={async () => {
              if (isProcessing || discountError || isApplyingPriceAdjustment) return;
              setIsProcessing(true);
              setDiscountError("");
              try {
                // setIsApplyingPriceAdjustment(true);
                // await applyPriceAdjustments(discountItemToPost);
                // setIsApplyingPriceAdjustment(false);
                const canProceedToPayment = await handleCreateSale();
                if (canProceedToPayment) {
                  onNext();
                  setIsBookingInfoUpdated(true);
                  setAppliedResponse(null);
                }
              } catch (error) {
                logger.error("Error proceeding to payment:", error);
                setDiscountError(error?.message || "Failed to proceed to payment");
                setIsProcessing(false);
                // setIsApplyingPriceAdjustment(false);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
